import { CHECKERBOARD } from '../../../utils/checkerboard';
import {
  FC,
  useRef,
  MutableRefObject,
  MouseEvent,
  TouchEvent
} from 'react';

import { useDragListeners } from '../../../utils/useDragListeners';
import { TPropsCompAlpha, TCoords } from './types';

const rgbaColor = (r: number, g: number, b: number, a: number) => {
  return `rgba(${[r, g, b, a / 100].join(',')})`;
};

const Alpha: FC<TPropsCompAlpha> = ({
  color,
  alpha,
  onChange,
  setChange
}) => {
  const node = useRef() as MutableRefObject<HTMLDivElement>;

  const drag = useDragListeners();

  const removeListeners = () => {
    drag.stop();
  };

  const removeTouchListeners = () => {
    setChange(false);
    drag.stop();
  };

  const onMouseDown = (e: MouseEvent) => {
    const x = e.clientX;
    const y = e.clientY;

    pointMoveTo({
      x,
      y
    });

    drag.start({ mousemove: onDrag, mouseup: onDragEnd });
  };

  const onDrag = (e: any) => {
    const x = e.clientX;
    const y = e.clientY;

    pointMoveTo({
      x,
      y
    });
  };

  const onDragEnd = (event: any) => {
    const x = event.clientX;
    const y = event.clientY;

    pointMoveTo({
      x,
      y
    });

    setChange(false);

    removeListeners();
  };

  const onTouchStart = (e: TouchEvent) => {
    if (e.cancelable) {
      e.preventDefault();
    }

    if (e.touches.length !== 1) {
      return;
    }

    removeTouchListeners();

    const x = e.targetTouches[0].clientX;
    const y = e.targetTouches[0].clientY;

    pointMoveTo({ x, y });

    drag.start(
      { touchmove: onTouchMove, touchend: onTouchEnd },
      { passive: false }
    );
  };

  const onTouchMove = (e: any) => {
    if (e.cancelable) {
      e.preventDefault();
    }

    const x = e.targetTouches[0].clientX;
    const y = e.targetTouches[0].clientY;

    pointMoveTo({
      x,
      y
    });
  };

  const onTouchEnd = () => {
    removeTouchListeners();
  };

  const getBackground = () => {
    const { red, green, blue } = color;
    const opacityGradient = `linear-gradient(to right, ${rgbaColor(
      red,
      green,
      blue,
      0
    )} , ${rgbaColor(red, green, blue, 100)})`;

    return opacityGradient;
  };

  const pointMoveTo = (coords: TCoords) => {
    const rect = node.current?.getBoundingClientRect();
    if (!rect) return;

    const width = rect.width;
    let left = coords.x - rect.left;

    left = Math.max(0, left);
    left = Math.min(left, width);

    const alpha = Math.round((left / width) * 100);

    onChange(alpha);
  };

  const getPointerBackground = () => {
    const { red, green, blue } = color;
    const alphaVal = (alpha || 1) / 100;

    return `rgba(${red}, ${green}, ${blue}, ${alphaVal})`;
  };

  return (
    <div
      className='relative h-full w-full select-none rounded'
      ref={node}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      style={{
        background: `linear-gradient(to right, transparent, black), ${CHECKERBOARD}`,
        backgroundSize: '100%, 6px'
      }}
    >
      <div
        className='absolute h-full w-full rounded'
        style={{ background: getBackground() }}
      />
      <span
        className='absolute top-[-3px] -ml-[7px] size-[14px] cursor-grab rounded-full border-2 border-white py-px shadow-[0_2px_4px_0_rgba(0,0,0,0.24)]'
        style={{
          left: `${alpha}%`,
          backgroundColor: getPointerBackground()
        }}
      />
      <div className='absolute h-full w-full cursor-grab' />
    </div>
  );
};

export default Alpha;
